import React from 'react';
import cx from 'classnames';

import makeStyles from '@mui/styles/makeStyles';
import Slide from '@mui/material/Slide';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';

// @mui/icons-material
import Close from '@mui/icons-material/Close';
import Person from '@mui/icons-material/Person';
import PersonOutline from '@mui/icons-material/PersonOutline';

import DA from '../store/ReduxAccess';

import KSelect from '../components/KSelect';
import CrmButton from './CrmButton';

import modalStyle from '../assets/jss/crm/modalStyle.jsx';

const useModalStyles = makeStyles(modalStyle);

/*
Cribbed from Material Dashboard's Notifications.jsx view

Call it with
  <MergeModal
    show={T/F}
    targetContactId={<whatever>}
    referrerOrgId={<whatever>}
    onConfirm={<function to call when 'confirm' button is clicked>}
    onCancel={<function to call when 'reject' button or 'close' idon is clicked>}
  />
*/

const Transition = React.forwardRef((props, ref) => <Slide direction="down" ref={ref} {...props} />);

const handleConfirm = (onConfirm, targetContactId, sourceContactId) => {
  onConfirm(targetContactId, sourceContactId);
};

export default function MergeModal({ show, targetContactId, referrerOrgId, onConfirm, onCancel }) {
  const [sourceContactId, setSourceContactId] = React.useState(0);
  const classes = useModalStyles();

  return (
    <div>
      <Dialog
        classes={{
          root: classes.center,
          paper: classes.modal,
        }}
        open={!!show}
        transition={Transition}
        keepMounted
        onClose={onCancel}
        aria-labelledby="modal-slide-title"
        aria-describedby="modal-slide-description"
      >
        <DialogTitle id="classic-modal-slide-title" className={classes.modalHeader}>
          <IconButton className={classes.modalCloseButton} onClick={onCancel} size="large">
            <Close className={classes.modalClose} />
          </IconButton>
          <h4 className={classes.modalTitle}>Merge Contacts</h4>
        </DialogTitle>
        <DialogContent id="modal-slide-description" className={classes.modalBody}>
          <MergePanel
            targetContactId={targetContactId}
            referrerOrgId={referrerOrgId}
            sourceContact={sourceContactId}
            setSourceContactFunc={setSourceContactId}
          />

          <ExplanationPanel targetContactId={targetContactId} sourceContact={sourceContactId} />
        </DialogContent>
        <DialogActions className={classes.modalFooter + ' ' + classes.modalFooterCenter}>
          <span>
            <b>Note:</b> This action cannot be undone
          </span>
          <CrmButton outlined onClick={onCancel}>
            Cancel
          </CrmButton>
          <CrmButton
            filled
            onClick={() => handleConfirm(onConfirm, targetContactId, sourceContactId)}
            disabled={!sourceContactId}
          >
            Merge
          </CrmButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const mergePanelStyles = (theme) => ({
  root: {
    display: 'flex',
  },
  spacerArrow: {
    margin: '60px 12px',
  },
});

const useMergePanelStyles = makeStyles(mergePanelStyles);

function MergePanel({ targetContactId, referrerOrgId, sourceContact, setSourceContactFunc }) {
  const classes = useMergePanelStyles();

  return (
    <div className={classes.root}>
      <SourceContactPanel
        targetContactId={targetContactId}
        referrerOrgId={referrerOrgId}
        sourceContact={sourceContact}
        setSourceContactFunc={setSourceContactFunc}
      />
      <div className={classes.spacerArrow}>---></div>
      <TargetContactPanel targetContactId={targetContactId} />
    </div>
  );
}

const panelW = 200;
const panelH = 150;
const iconW = 60;
const iconH = 60;
const iconMarginL = (panelW - iconW) / 2;
const lightBorder = '1px solid #ccc';

const sourcePanelStyles = (theme) => ({
  root: {
    width: panelW + 'px',
    height: panelH + 'px',
    border: lightBorder,
  },
  iconDiv: {
    width: iconW + 'px',
    height: iconH + 'px',
    marginLeft: iconMarginL + 'px',
    textAlign: 'center',
    marginTop: '24px',
    // border: lightBorder,
  },
  icon: {
    fontSize: '72px',
  },
  hidden: {
    visibility: 'hidden',
  },
  dropdownDiv: {
    margin: '8px 10px 0 10px',
  },
});

const useSourcePanelStyles = makeStyles(sourcePanelStyles);

function SourceContactPanel({ targetContactId, referrerOrgId, sourceContact, setSourceContactFunc }) {
  const classes = useSourcePanelStyles();
  const allContacts = DA.getContactsAtVisibleFirm(referrerOrgId);
  const allOtherContacts = allContacts.filter((c) => c.personId !== targetContactId);
  const realOptions = allOtherContacts.map((c) => c.preferredFullName);
  const realValues = allOtherContacts.map((c) => c.personId);
  const optionsToShow = ['Choose Contact to merge...', ...realOptions];
  const valuesToShow = [0, ...realValues];

  return (
    <div className={classes.root}>
      <div className={cx([classes.iconDiv, { [classes.hidden]: !sourceContact }])}>
        <PersonOutline className={classes.icon} />
      </div>
      <div className={classes.dropdownDiv}>
        <KSelect
          fullWidth
          liveOptions={optionsToShow}
          liveValues={valuesToShow}
          value={sourceContact}
          onChange={(event) => setSourceContactFunc(event.target.value)}
        />
      </div>
    </div>
  );
}

const targetPanelStyles = (theme) => ({
  root: {
    width: panelW + 'px',
    height: panelH + 'px',
    border: lightBorder,
  },
  iconDiv: {
    width: iconW + 'px',
    height: iconH + 'px',
    marginLeft: iconMarginL + 'px',
    textAlign: 'center',
    marginTop: '24px',
    // border: lightBorder,
  },
  icon: {
    fontSize: '72px',
  },
  targetName: {
    marginTop: '12px',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  targetEmail: {
    textAlign: 'center',
    color: '#999',
  },
});

const useTargetPanelStyles = makeStyles(targetPanelStyles);

function TargetContactPanel({ targetContactId }) {
  const classes = useTargetPanelStyles();
  const target = DA.getPersonById(targetContactId);
  if (target) {
    return (
      <div className={classes.root}>
        <div className={classes.iconDiv}>
          <Person className={classes.icon} />
        </div>
        <div className={classes.targetName}>{target.preferredFullName}</div>
        <div className={classes.targetEmail}>{target.email}</div>
      </div>
    );
  } else {
    return null;
  }
}

const explanPanelStyles = (theme) => ({
  root: {
    marginTop: '10px',
  },
  hidden: {
    // visibility: "hidden",
    display: 'none',
  },
  mergeLine: {
    textAlign: 'center',
  },
  mergeBridge: {
    fontWeight: 'bold',
  },
  explanation: {
    marginTop: '4px',
    padding: '2px 8px 2px 0',
    backgroundColor: '#dfe',
    fontSize: '85%',
  },
});

const useExplanPanelStyles = makeStyles(explanPanelStyles);

function ExplanationPanel({ sourceContact, targetContactId }) {
  const classes = useExplanPanelStyles();
  const sourcePerson = sourceContact ? DA.getPersonById(sourceContact) : null;
  const sourcePersonName = sourcePerson ? sourcePerson.preferredFullName : '';
  const targetPerson = DA.getPersonById(targetContactId);

  if (targetPerson) {
    return (
      <div className={cx([classes.root, { [classes.hidden]: !sourceContact }])}>
        <div className={classes.mergeLine}>
          {sourcePersonName} <span className={classes.mergeBridge}>will be merged into</span>{' '}
          {targetPerson.preferredFullName}
        </div>
        <div className={classes.explanation}>
          <ol>
            <li>The resulting contact record will use the email: {targetPerson.email}</li>
            <li>Timeline activity for both contacts will be preserved and merged</li>
            <li>etc.</li>
          </ol>
        </div>
      </div>
    );
  } else {
    return null;
  }
}
